import React from 'react';
import styled from 'styled-components';
import { CHART_COLORS } from '../../config/skillsCategories';

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  position: relative;
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 100%;
`;

const ChartAxis = styled.line`
  stroke: var(--border-color);
  stroke-width: 1;
`;

const ChartGrid = styled.line`
  stroke: var(--border-color);
  stroke-width: 0.5;
  opacity: 0.3;
`;

const ChartBar = styled.rect<{ color: string }>`
  fill: ${props => props.color};
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ChartLabel = styled.text`
  font-size: 12px;
  fill: var(--text-secondary);
  text-anchor: middle;
`;

const ChartYLabel = styled.text`
  font-size: 12px;
  fill: var(--text-secondary);
  text-anchor: end;
`;

const ChartValue = styled.text`
  font-size: 11px;
  fill: var(--text-primary);
  text-anchor: middle;
  font-weight: 600;
`;

interface BarData {
  label: string;
  value: number;
  color?: string;
}

interface BarChartProps {
  data: BarData[];
  width?: number;
  height?: number;
  yMin?: number;
  yMax?: number;
}

export const BarChart: React.FC<BarChartProps> = ({
  data,
  width = 400,
  height = 300,
  yMin = 0,
  yMax = 10
}) => {
  const margin = { top: 20, right: 20, bottom: 60, left: 40 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const barWidth = chartWidth / data.length * 0.8;
  const barSpacing = chartWidth / data.length * 0.2;

  // Default colors for bars
  const defaultColors = CHART_COLORS;

  return (
    <ChartContainer>
      <ChartSvg viewBox={`0 0 ${width} ${height}`}>
        {/* Y-axis */}
        <ChartAxis
          x1={margin.left}
          y1={margin.top}
          x2={margin.left}
          y2={height - margin.bottom}
        />
        
        {/* X-axis */}
        <ChartAxis
          x1={margin.left}
          y1={height - margin.bottom}
          x2={width - margin.right}
          y2={height - margin.bottom}
        />

        {/* Y-axis grid lines and labels */}
        {[0, 2, 4, 6, 8, 10].map(value => {
          const y = height - margin.bottom - (value / yMax) * chartHeight;
          return (
            <g key={value}>
              <ChartGrid
                x1={margin.left}
                y1={y}
                x2={width - margin.right}
                y2={y}
              />
              <ChartYLabel x={margin.left - 10} y={y + 4}>
                {value}
              </ChartYLabel>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((item, index) => {
          const x = margin.left + (index * (barWidth + barSpacing)) + barSpacing / 2;
          const barHeight = (item.value / yMax) * chartHeight;
          const y = height - margin.bottom - barHeight;
          const color = item.color || defaultColors[index % defaultColors.length];

          return (
            <g key={index}>
              <ChartBar
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                color={color}
              />
              
              {/* Value label on top of bar */}
              <ChartValue
                x={x + barWidth / 2}
                y={y - 5}
              >
                {item.value.toFixed(1)}
              </ChartValue>
              
              {/* X-axis label */}
              <ChartLabel
                x={x + barWidth / 2}
                y={height - margin.bottom + 20}
              >
                {item.label}
              </ChartLabel>
            </g>
          );
        })}
      </ChartSvg>
    </ChartContainer>
  );
};